import { useState, useEffect } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { supabase } from '../../../lib/supabase';
import { mockTeamMembers, mockActionLogs, Settings } from '../../../mocks/step6Data';

const statusColors: Record<string, string> = {
  aktif: 'bg-green-500',
  pasif: 'bg-red-500',
  ilerliyor: 'bg-yellow-500',
  tamamladi: 'bg-blue-500',
};

const statusLabels: Record<string, string> = {
  aktif: 'Aktif',
  pasif: 'Pasif',
  ilerliyor: 'İlerliyor',
  tamamladi: 'Tamamladı',
};

const actionIcons: Record<string, string> = {
  message: 'ri-whatsapp-line',
  update: 'ri-refresh-line',
  add: 'ri-user-add-line',
  note: 'ri-sticky-note-line',
};

const focusSuggestions = [
  { text: 'Bugün Zeynep Kara\'ya aktiflik hatırlatması gönder', icon: 'ri-alarm-warning-line', color: 'text-red-400', action: 'WhatsApp Mesaj Gönder' },
  { text: 'Ahmet Yılmaz 1. adımda bekliyor, iletişime geç', icon: 'ri-footprint-line', color: 'text-amber-400', action: 'Detaya Git' },
  { text: 'Ekibini 4/4\'e tamamlamak için 1 kişi daha ekle', icon: 'ri-user-add-line', color: 'text-blue-400', action: 'Kişi Ekle' },
];

interface DashboardProps {
  onNavigate: (section: string) => void;
  settings: Settings;
}

interface TreeNode {
  id: string;
  name: string;
  phone: string;
  status: 'aktif' | 'pasif' | 'ilerliyor' | 'tamamladi';
  step1Completed: boolean;
  registered: boolean;
  children: TreeNode[];
  level: number;
}

export default function Dashboard({ onNavigate, settings }: DashboardProps) {
  const { profile } = useAuth();
  const [teamMembers, setTeamMembers] = useState(mockTeamMembers);
  const [actionLogs, setActionLogs] = useState(mockActionLogs);
  const [teamTree, setTeamTree] = useState<TreeNode[]>([]);
  const [treeLoading, setTreeLoading] = useState(true);

  const userRole = profile?.role || 'kullanici';

  useEffect(() => {
    if (!profile) return;

    let cancelled = false;

    supabase
      .from('step6_team_members')
      .select('*')
      .eq('user_id', profile.id)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (data && data.length > 0) {
          const mapped = data.map((m: Record<string, unknown>) => ({
            id: m.id as string,
            name: m.name as string,
            phone: m.phone as string,
            status: m.status as 'aktif' | 'pasif' | 'ilerliyor' | 'tamamladi',
            step1Completed: m.step1_completed as boolean,
            lastContact: m.last_contact as string,
            note: m.note as string,
            joinDate: m.join_date as string,
          }));
          setTeamMembers(mapped);
        }
      });

    supabase
      .from('step6_action_logs')
      .select('*')
      .eq('user_id', profile.id)
      .order('created_at', { ascending: false })
      .limit(5)
      .then(({ data, error }) => {
        if (cancelled) return;
        if (data && data.length > 0) {
          setActionLogs(data.map((l: Record<string, unknown>) => ({
            id: l.id as string,
            personName: l.person_name as string,
            action: l.action as string,
            date: new Date(l.created_at as string).toLocaleString('tr-TR'),
            type: l.type as 'message' | 'update' | 'add' | 'note',
          })));
        }
      });

    return () => { cancelled = true; };
  }, [profile]);

  // Build team tree for leaders
  useEffect(() => {
    if (!profile) {
      setTreeLoading(false);
      return;
    }

    let cancelled = false;

    async function buildTree() {
      try {
        // Fetch top-level team members
        const { data: directMembers } = await supabase
          .from('step6_team_members')
          .select('*')
          .eq('user_id', profile.id)
          .order('sort_order', { ascending: true });

        if (cancelled || !directMembers || directMembers.length === 0) {
          if (!cancelled) setTreeLoading(false);
          return;
        }

        const treeNodes: TreeNode[] = [];

        for (const dm of directMembers) {
          const node: TreeNode = {
            id: dm.id,
            name: dm.name,
            phone: dm.phone,
            status: dm.status as TreeNode['status'],
            step1Completed: dm.step1_completed,
            registered: !!dm.member_user_id,
            children: [],
            level: 0,
          };

          // If this member has registered, fetch their sub-team
          if (dm.member_user_id) {
            const { data: subMembers } = await supabase
              .from('step6_team_members')
              .select('*')
              .eq('user_id', dm.member_user_id)
              .order('sort_order', { ascending: true });

            if (subMembers && subMembers.length > 0) {
              node.children = subMembers.map((sm: Record<string, unknown>) => ({
                id: sm.id as string,
                name: sm.name as string,
                phone: sm.phone as string,
                status: sm.status as TreeNode['status'],
                step1Completed: (sm.step1_completed as boolean) || false,
                registered: !!(sm.member_user_id as string),
                children: [],
                level: 1,
              }));
            }
          }

          treeNodes.push(node);
        }

        if (!cancelled) {
          setTeamTree(treeNodes);
          setTreeLoading(false);
        }
      } catch {
        if (!cancelled) setTreeLoading(false);
      }
    }

    buildTree();

    return () => { cancelled = true; };
  }, [profile]);

  const step1Count = teamMembers.filter(m => m.step1Completed).length;
  const activeCount = teamMembers.filter(m => m.status === 'aktif' || m.status === 'tamamladi').length;
  const estimatedIncome = activeCount * settings.seviye1Kazanc + step1Count * settings.kayitPrimi;
  const coveragePercent = Math.min(100, Math.round((estimatedIncome / settings.aktiflikBedeli) * 100));

  const kpis = [
    {
      title: 'Aktiflik Durumu',
      value: 'Aktif',
      sub: '8 gün kaldı',
      icon: 'ri-shield-check-line',
      color: 'text-green-400',
      bg: 'rgba(34,197,94,0.08)',
    },
    {
      title: 'İlk 4 Kişi',
      value: `${teamMembers.length}/4`,
      sub: `${teamMembers.length === 4 ? 'Tamamlandı' : 'Eksik: ' + (4 - teamMembers.length)}`,
      icon: 'ri-team-line',
      color: 'text-amber-400',
      bg: 'rgba(245,166,35,0.08)',
    },
    {
      title: '1. Adım Tamamlayan',
      value: `${step1Count}/4`,
      sub: `${4 - step1Count} kişi bekliyor`,
      icon: 'ri-checkbox-circle-line',
      color: 'text-blue-400',
      bg: 'rgba(0,180,255,0.08)',
    },
    {
      title: 'Tahmini Bu Ay Gelir',
      value: `₺${estimatedIncome.toLocaleString('tr-TR')}`,
      sub: 'Mevcut ekiple hesaplandı',
      icon: 'ri-money-dollar-circle-line',
      color: 'text-purple-400',
      bg: 'rgba(168,85,247,0.08)',
    },
    {
      title: 'Aktiflik Karşılama',
      value: `%${coveragePercent}`,
      sub: coveragePercent >= 100 ? 'Aktiflik karşılandı!' : `₺${(settings.aktiflikBedeli - estimatedIncome).toLocaleString('tr-TR')} eksik`,
      icon: 'ri-pie-chart-2-line',
      color: coveragePercent >= 100 ? 'text-green-400' : 'text-orange-400',
      bg: coveragePercent >= 100 ? 'rgba(34,197,94,0.08)' : 'rgba(255,107,53,0.08)',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">24 Mart 2026, Salı — Günlük özet</p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-amber-400" style={{ background: 'rgba(245,166,35,0.1)' }}>
          <i className="ri-calendar-line text-base" />
          Mart 2026
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {kpis.map((kpi, i) => (
          <div key={i} className="rounded-2xl p-4 transition-all duration-200 hover:translate-y-[-2px]"
            style={{ background: '#12141E', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="flex items-start justify-between mb-3">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: kpi.bg }}>
                <i className={`${kpi.icon} text-base ${kpi.color}`} />
              </div>
            </div>
            <div className={`text-xl font-bold ${kpi.color} mb-0.5`}>{kpi.value}</div>
            <div className="text-xs font-medium text-gray-300 mb-1">{kpi.title}</div>
            <div className="text-xs text-gray-600">{kpi.sub}</div>
          </div>
        ))}
      </div>

      {/* Focus Panel + Team Cards Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Bugünkü Odak */}
        <div className="lg:col-span-1 rounded-2xl p-5" style={{ background: '#12141E', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(245,166,35,0.12)' }}>
              <i className="ri-focus-3-line text-amber-400 text-sm" />
            </div>
            <h2 className="text-sm font-bold text-white">Bugünkü Odak</h2>
          </div>
          <div className="space-y-3">
            {focusSuggestions.map((s, i) => (
              <div key={i} className="rounded-xl p-3 flex items-start gap-3" style={{ background: 'rgba(255,255,255,0.03)' }}>
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <i className={`${s.icon} text-sm ${s.color}`} />
                </div>
                <p className="text-xs text-gray-300 flex-1 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
          <button onClick={() => onNavigate('whatsapp')}
            className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold text-white cursor-pointer transition-all duration-200 hover:opacity-90 whitespace-nowrap"
            style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}>
            <i className="ri-whatsapp-line text-base" />
            WhatsApp Mesaj Gönder
          </button>
        </div>

        {/* İlk 4 Kişi Mini Kartlar */}
        <div className="lg:col-span-2 rounded-2xl p-5" style={{ background: '#12141E', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(0,180,255,0.12)' }}>
                <i className="ri-team-line text-blue-400 text-sm" />
              </div>
              <h2 className="text-sm font-bold text-white">İlk 4 Kişim</h2>
            </div>
            <button onClick={() => onNavigate('ilk4')} className="text-xs text-amber-400 hover:text-amber-300 cursor-pointer whitespace-nowrap">Tümünü Gör →</button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {teamMembers.map(member => (
              <div key={member.id} className="rounded-xl p-3 flex items-start gap-3 transition-all duration-200 hover:bg-white/5 cursor-pointer"
                style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold text-white"
                  style={{ background: 'linear-gradient(135deg, #F5A623 0%, #FF6B35 100%)' }}>
                  {member.name.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-white truncate">{member.name}</span>
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${statusColors[member.status]}`} />
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-gray-500">{statusLabels[member.status]}</span>
                    {member.step1Completed
                      ? <span className="text-xs text-blue-400 flex items-center gap-1"><i className="ri-check-line" />1. Adım</span>
                      : <span className="text-xs text-red-400 flex items-center gap-1"><i className="ri-close-line" />1. Adım</span>}
                  </div>
                </div>
                <div className="flex flex-col gap-1.5">
                  <button className="w-7 h-7 rounded-lg flex items-center justify-center cursor-pointer hover:bg-green-500/20 transition-colors" style={{ background: 'rgba(255,255,255,0.05)' }}>
                    <i className="ri-whatsapp-line text-green-400 text-sm" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* STEP6 Progress */}
        <div className="rounded-2xl p-5" style={{ background: '#12141E', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(245,166,35,0.12)' }}>
              <i className="ri-bar-chart-grouped-line text-amber-400 text-sm" />
            </div>
            <h2 className="text-sm font-bold text-white">STEP6 İlerlemem</h2>
          </div>
          <div className="flex gap-2 mb-4">
            {[1,2,3,4,5,6].map(step => (
              <div key={step} className="flex-1 flex flex-col items-center gap-1">
                <div className={`w-full h-8 rounded-lg flex items-center justify-center text-xs font-bold transition-all ${
                  step === 1 ? 'text-white' : 'text-gray-600'
                }`} style={{ background: step === 1 ? 'linear-gradient(135deg, #F5A623 0%, #FF6B35 100%)' : 'rgba(255,255,255,0.05)' }}>
                  {step}
                </div>
                <span className="text-xs text-gray-600">{step === 1 ? 'Mevcut' : ''}</span>
              </div>
            ))}
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>
            <div className="h-full rounded-full transition-all duration-700" style={{ width: '18%', background: 'linear-gradient(90deg, #F5A623 0%, #FF6B35 100%)' }} />
          </div>
          <p className="text-xs text-gray-500 mt-2">1. Adım tamamlanıyor — %18 genel ilerleme</p>
        </div>

        {/* Son Aksiyonlar */}
        <div className="rounded-2xl p-5" style={{ background: '#12141E', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(168,85,247,0.12)' }}>
              <i className="ri-history-line text-purple-400 text-sm" />
            </div>
            <h2 className="text-sm font-bold text-white">Son Aksiyonlar</h2>
          </div>
          <div className="space-y-3">
            {actionLogs.slice(0, 4).map(log => (
              <div key={log.id} className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(255,255,255,0.04)' }}>
                  <i className={`${actionIcons[log.type]} text-sm text-gray-400`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-300"><span className="font-semibold text-white">{log.personName}</span> — {log.action}</p>
                  <p className="text-xs text-gray-600 mt-0.5">{log.date}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Ekip Ağacı — Tüm kullanıcılar görür */}
      <div className="rounded-2xl p-5" style={{ background: '#12141E', border: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'rgba(34,197,94,0.12)' }}>
            <i className="ri-git-branch-line text-green-400 text-sm" />
          </div>
          <h2 className="text-sm font-bold text-white">Ekip Ağacı</h2>
          <span className="text-xs text-gray-600 ml-auto">
            {teamTree.reduce((sum, n) => sum + 1 + n.children.length, 0)} kişi
          </span>
        </div>

        {treeLoading ? (
          <div className="flex items-center justify-center py-10">
            <div className="w-6 h-6 rounded-full border-2 border-green-400 border-t-transparent animate-spin" />
          </div>
        ) : teamTree.length === 0 ? (
          <div className="text-center py-10">
            <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3" style={{ background: 'rgba(255,255,255,0.04)' }}>
              <i className="ri-team-line text-2xl text-gray-500" />
            </div>
            <p className="text-sm text-gray-500 mb-1">Henüz ekip üyen yok</p>
            <button onClick={() => onNavigate('ilk4')}
              className="text-xs text-amber-400 hover:text-amber-300 font-medium cursor-pointer whitespace-nowrap">
              İlk 4 kişini ekle →
            </button>
          </div>
        ) : (
          <div className="space-y-0">
            {/* Top-level members */}
            {teamTree.map((node) => (
              <div key={node.id}>
                <div className="flex items-center gap-3 py-2.5 px-3 rounded-xl transition-colors hover:bg-white/5">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                    style={{ background: node.registered ? 'linear-gradient(135deg, #34C759 0%, #30B350 100%)' : 'linear-gradient(135deg, #F5A623 0%, #FF6B35 100%)' }}>
                    {node.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-white truncate">{node.name}</span>
                      {node.registered && <i className="ri-verified-badge-fill text-green-400 text-xs flex-shrink-0" title="Kayıt oldu" />}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${statusColors[node.status]}`} />
                      <span className="text-xs text-gray-500">{statusLabels[node.status]}</span>
                      {node.step1Completed && <span className="text-xs text-blue-400">· 1. Adım</span>}
                    </div>
                  </div>
                  {node.children.length > 0 && (
                    <span className="text-xs text-gray-600 flex-shrink-0">{node.children.length} alt ekip</span>
                  )}
                  {!node.registered && (
                    <span className="text-xs text-amber-400 bg-amber-400/10 px-2 py-0.5 rounded-full flex-shrink-0">Davette</span>
                  )}
                </div>

                {/* Sub-team members */}
                {node.children.length > 0 && (
                  <div className="ml-8 pl-4" style={{ borderLeft: '2px solid rgba(255,255,255,0.06)' }}>
                    {node.children.map((child) => (
                      <div key={child.id}
                        className="flex items-center gap-3 py-2 px-3 rounded-xl transition-colors hover:bg-white/5 relative">
                        <div className="absolute left-[-1.15rem] top-1/2 w-3 h-px" style={{ background: 'rgba(255,255,255,0.08)' }} />
                        <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                          style={{ background: child.registered ? 'linear-gradient(135deg, #34C759 0%, #30B350 100%)' : 'linear-gradient(135deg, #6B7280 0%, #374151 100%)' }}>
                          {child.name.charAt(0)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-300 truncate">{child.name}</span>
                            {child.registered && <i className="ri-verified-badge-fill text-green-400 text-xs flex-shrink-0" />}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${statusColors[child.status]}`} />
                            <span className="text-xs text-gray-500">{statusLabels[child.status]}</span>
                          </div>
                        </div>
                        {!child.registered && (
                          <span className="text-xs text-amber-400 bg-amber-400/10 px-2 py-0.5 rounded-full flex-shrink-0">Davette</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
